// Builds the reserved `_submission_meta` bundle injected into a survey's
// result_json at submission time (card #2831) -- lets ops identify which
// responses came through WIN Field App v2 without inferring it from image
// bucket contents, following the same "namespaced key in result_json"
// precedent Response#extract_stock_events already established for
// stock_event_id markers.
//
// New leaf module (no imports from queue/sync/controller modules), alongside
// field_network_health.js and field_sync_shared.js -- both result_editor_
// controller.js and field_upload_sync.js import this one, matching the "one
// implementation, not two" convention already used for level-detection code.
//
// Field-app-only by design: app/views/surveys/results/_edit.html.erb (and its
// survey-result-editor controller) is shared between the field app and the
// admin/rt-win survey editor. The `app-version` meta tag is only rendered by
// the field layout (app/views/layouts/field.html.erb), so its absence is the
// signal that this page is NOT the field app -- _submission_meta is skipped
// entirely rather than recorded with a null app_version.

import { currentLevel, LEVEL_OFFLINE, LEVEL_DEGRADED, LEVEL_ONLINE } from "./field_network_health"

const LEVEL_LABEL = {
  [LEVEL_OFFLINE]:  "offline",
  [LEVEL_DEGRADED]: "degraded",
  [LEVEL_ONLINE]:   "online"
}

// Photo-type questions, mirroring Response::CAMERA_QUESTION_TYPES server-side
// (app/models/response.rb) -- "file" is included there too because
// convertPhotoTypeQuestionsToFileUpload (result_editor_controller.js) already
// normalises camera-upload-question/camera-mult-upload-question to "file"
// (+ a cameraMode marker) before the survey is rendered, so by submit time
// every photo question -- single or multi -- presents as type "file".
const PHOTO_QUESTION_TYPE = "file"

function _hasDocument() {
  return typeof document !== "undefined"
}

// Reads the app-version meta tag rendered only by the field layout. Returns
// null off the field app (e.g. the admin survey editor) or in a context with
// no document (this module is never imported by the service worker, but the
// guard costs nothing and matches the defensive style of field_network_health.js).
function fieldAppVersion() {
  if (!_hasDocument()) return null
  return document.querySelector('meta[name="app-version"]')?.content || null
}

export function isFieldApp() {
  return fieldAppVersion() !== null
}

function _isPopulated(value) {
  return value !== null && value !== undefined && value !== ""
}

// Sums populated answers across the survey's photo-type questions, including
// multi-photo array-type questions (a question can hold an array of several
// images rather than a single value) -- the client-side analog of
// Response#process_pwa_images!'s value.is_a?(Array) handling for the same
// shape.
export function expectedImageCount(survey, data) {
  if (!survey || typeof survey.getAllQuestions !== "function" || !data) return 0

  return survey.getAllQuestions()
    .filter(q => q.getType() === PHOTO_QUESTION_TYPE)
    .reduce((count, q) => {
      const value = data[q.name]
      if (Array.isArray(value)) return count + value.filter(_isPopulated).length
      return count + (_isPopulated(value) ? 1 : 0)
    }, 0)
}

// Builds a fresh _submission_meta bundle for a submission attempt that has
// not yet been queued/retried (retry_count starts at 0). Returns null off the
// field app -- callers must not attach _submission_meta in that case.
//
// captured_at (card #2872) is stamped ONCE here, at the moment the survey
// data is first snapshotted for sending -- it is the server's only signal
// for distinguishing a fresh save from a stale queued replay (e.g. a
// "save and back" PUT that lands after the response has already been
// completed with newer data). It must NEVER be regenerated on a later
// attempt: refreshRetryMeta below deliberately does not touch it.
export function buildSubmissionMeta(survey, data) {
  if (!isFieldApp()) return null

  return {
    app: "field_app_v2",
    app_version: fieldAppVersion(),
    network_level: LEVEL_LABEL[currentLevel()] ?? null,
    retry_count: 0,
    expected_image_count: expectedImageCount(survey, data),
    captured_at: new Date().toISOString()
  }
}

// Refreshes retry_count (and network_level, since connectivity may have
// changed between attempts) on an already-queued payload immediately before a
// replay PUT (field_upload_sync.js#replayResponse). retryCount counts retries
// of THIS save specifically (record.consecutiveFailures) -- image-upload
// retries are a separate, independent ladder (field_image_queue.js) and are
// not conflated here.
//
// Returns a new object -- never mutates the caller's data -- so a caller that
// holds onto the original reference (e.g. record.surveyData) is unaffected.
// A payload with no _submission_meta (queued before this card shipped, or
// never on the field app) is returned unchanged.
//
// Deliberately does NOT touch captured_at (card #2872) -- unlike retry_count
// and network_level, captured_at must stay fixed at whatever moment the
// original snapshot was taken, or the server's staleness check would see a
// replay as fresh every time it re-fires. The spread of ...data._submission_meta
// below preserves it untouched.
export function refreshRetryMeta(data, retryCount) {
  if (!data || !data._submission_meta) return data

  return {
    ...data,
    _submission_meta: {
      ...data._submission_meta,
      retry_count: retryCount,
      network_level: LEVEL_LABEL[currentLevel()] ?? data._submission_meta.network_level
    }
  }
}
